/**
 * «Добавить все слова» в наборе слов урока — очередью, а не всеми запросами разом (промпт 12, 18.10.2026).
 *
 * ⚠️ ЗАЧЕМ. Раньше кнопка слала по запросу на каждое слово одновременно. На большом наборе это упиралось в два
 * разных отказа vocab-service, и оба ученик видел как «не удалось — попробуйте ещё раз»:
 * - ограничитель частоты `vocab_add` (120 в минуту): 429 `{error:"rate limit exceeded", retry_after, bucket}`.
 *   Повтор через `retry_after` проходит — очередь ждёт и продолжает сама;
 * - дневной лимит новых слов (`VOCAB_DAILY_NEW_WORDS`, 50): 429 с русским текстом без кода (сервер позже
 *   добавит код `daily_new_words` — шаг 2б промпта 12). Повтор этого слова до завтра не поможет — оно получает
 *   «лимит», у него остаётся кнопка. Очередь НЕ встаёт: лимит тратят только слова, которых нет в общем словаре,
 *   а уже известные сервер принимает и дальше (его текст: «слова, которые уже есть в словаре, можно добавлять и
 *   дальше»). Найдено на стенде 18.10.2026: из 60 слов новым оказалось одно, и первая версия очереди бросила бы
 *   остальные.
 *
 * Фронт понимает и нынешний ответ сервера, и будущий с кодом — от выката сервера не зависит.
 */

import { apiErrorCode } from '@/lib/apiErrorCode';

export type WordAddFailure =
  | { kind: 'tooFast'; retryAfterSec?: number }
  | { kind: 'limit' }
  | { kind: 'other' };

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Почему не добавилось слово: частота (подождать), дневной лимит (завтра) или прочее. */
export function classifyWordAddError(err: unknown): WordAddFailure {
  if (!isObject(err) || err.status !== 429) return { kind: 'other' };
  const body = isObject(err.raw) ? err.raw : {};
  const code = apiErrorCode(err);
  if (code === 'daily_new_words' || code === 'daily_limit') return { kind: 'limit' };
  const tooFast = code === 'rate_limited' || body.error === 'rate limit exceeded' || typeof body.bucket === 'string';
  if (tooFast) {
    const ra = Number(body.retry_after);
    return { kind: 'tooFast', retryAfterSec: Number.isFinite(ra) && ra > 0 ? ra : undefined };
  }
  // Иной 429 у этой ручки — дневной лимит новых слов (текст без кода, как отдаёт сервер сейчас).
  return { kind: 'limit' };
}

export type QueueOutcome = 'done' | 'error' | 'limit';

export type AddQueueOptions = {
  /** Сколько запросов в полёте одновременно. */
  concurrency?: number;
  /** Сколько раз ждать «слишком часто» на одном слове, прежде чем сдаться. */
  maxWaits?: number;
  /** Пауза по умолчанию, если сервер не сказал `retry_after`, и предел паузы — в секундах. */
  defaultWaitSec?: number;
  maxWaitSec?: number;
  onResult?: (index: number, outcome: QueueOutcome) => void;
  sleep?: (ms: number) => Promise<void>;
};

/**
 * Прогоняет `indices` через `add` не больше `concurrency` за раз. Слово на дневном лимите получает «лимит», очередь
 * идёт дальше (известные слова сервер принимает). Возвращает, упёрлось ли хоть одно слово в лимит.
 */
export async function runAddQueue(
  indices: number[],
  add: (index: number) => Promise<void>,
  opts: AddQueueOptions = {},
): Promise<{ limitHit: boolean }> {
  const {
    concurrency = 4,
    maxWaits = 3,
    defaultWaitSec = 5,
    maxWaitSec = 60,
    onResult,
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  } = opts;
  let next = 0;
  let limitHit = false;

  const one = async (i: number) => {
    for (let waits = 0; ; waits++) {
      try {
        await add(i);
        onResult?.(i, 'done');
        return;
      } catch (err) {
        const f = classifyWordAddError(err);
        if (f.kind === 'limit') {
          limitHit = true;
          onResult?.(i, 'limit');
          return;
        }
        if (f.kind === 'tooFast' && waits < maxWaits) {
          await sleep(Math.min(f.retryAfterSec ?? defaultWaitSec, maxWaitSec) * 1000);
          continue;
        }
        onResult?.(i, 'error');
        return;
      }
    }
  };

  const worker = async () => {
    while (next < indices.length) {
      const i = indices[next++];
      await one(i);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, indices.length) }, worker));
  return { limitHit };
}
